import { processPoiSuggestion } from './poi.processor.js'
import { createLogger } from '../../common/utils/logger.js'
import { calculateDistance, geohash } from '@my-roadtrip/utils'

const logger = createLogger('CacheWarmer')

/**
 * Popular routes for cache warming
 * Pre-generate POI suggestions during off-peak hours (3-6am)
 */
const POPULAR_ROUTES = [
  // France - Routes principales
  { from: { lat: 48.8566, lng: 2.3522, name: 'Paris' }, to: { lat: 45.7640, lng: 4.8357, name: 'Lyon' }, categories: ['restaurant', 'viewpoint', 'castle'] },
  { from: { lat: 45.7640, lng: 4.8357, name: 'Lyon' }, to: { lat: 43.2965, lng: 5.3698, name: 'Marseille' }, categories: ['restaurant', 'beach', 'viewpoint'] },
  { from: { lat: 43.2965, lng: 5.3698, name: 'Marseille' }, to: { lat: 43.7102, lng: 7.2620, name: 'Nice' }, categories: ['beach', 'restaurant', 'viewpoint'] },
  { from: { lat: 48.5734, lng: 7.7521, name: 'Strasbourg' }, to: { lat: 48.8566, lng: 2.3522, name: 'Paris' }, categories: ['restaurant', 'castle', 'museum'] },
  { from: { lat: 47.2184, lng: -1.5536, name: 'Nantes' }, to: { lat: 44.8378, lng: -0.5792, name: 'Bordeaux' }, categories: ['beach', 'restaurant', 'winery'] },
  
  // Routes secondaires populaires
  { from: { lat: 43.7656, lng: 4.2464, name: 'Lunel' }, to: { lat: 44.1361, lng: 4.8083, name: 'Orange' }, categories: ['restaurant', 'castle', 'viewpoint'] },
  { from: { lat: 44.8378, lng: -0.5792, name: 'Bordeaux' }, to: { lat: 43.6047, lng: 1.4442, name: 'Toulouse' }, categories: ['restaurant', 'winery', 'viewpoint'] },
  { from: { lat: 48.1173, lng: -1.6778, name: 'Rennes' }, to: { lat: 48.3905, lng: -4.4861, name: 'Brest' }, categories: ['beach', 'restaurant', 'viewpoint'] },
  { from: { lat: 47.4716, lng: -0.5519, name: 'Angers' }, to: { lat: 47.3941, lng: 0.6848, name: 'Tours' }, categories: ['castle', 'restaurant', 'winery'] },
  { from: { lat: 45.1885, lng: 5.7245, name: 'Grenoble' }, to: { lat: 44.9334, lng: 4.8924, name: 'Valence' }, categories: ['mountain', 'restaurant', 'viewpoint'] }
]

/**
 * Calculate midpoint between two locations
 */
function calculateMidpoint(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): { lat: number; lng: number } {
  return {
    lat: (from.lat + to.lat) / 2,
    lng: (from.lng + to.lng) / 2
  }
}

/**
 * Warm cache for popular routes
 * Should be run during off-peak hours (e.g., 3am daily)
 */
export async function warmCache() {
  const startTime = Date.now()
  logger.info('Starting cache warming', { routesCount: POPULAR_ROUTES.length })

  let successCount = 0
  let errorCount = 0

  for (const route of POPULAR_ROUTES) {
    try {
      const midpoint = calculateMidpoint(route.from, route.to)
      const distance = calculateDistance(route.from, route.to)
      const radiusKm = Math.min(Math.ceil(distance / 2000), 100) // Half distance in km, max 100km

      // Generate cache key using geohash
      const geoHash = geohash(midpoint.lat, midpoint.lng, 6)
      const radiusBucket = Math.ceil(radiusKm / 25) * 25
      const categories = [...route.categories].sort()
      const cacheKey = `ai:poi:geo:${geoHash}:r${radiusBucket}:${categories.join('|')}`

      logger.debug('Warming cache for route', {
        from: route.from.name,
        to: route.to.name,
        midpoint,
        radiusKm,
        categories: route.categories,
        cacheKey
      })

      // Process POI suggestion (will cache result)
      await processPoiSuggestion({
        userId: 'cache-warmer',
        userTier: 'premium', // Use best quality for popular routes
        location: midpoint,
        categories: route.categories,
        radiusKm,
        cacheKey,
        planId: 'cache-warmer',
        stageId: 'cache-warmer',
        planName: `${route.from.name} → ${route.to.name}`,
        stageName: 'Cache Warming'
      })

      successCount++
      logger.info('Cache warmed successfully', {
        route: `${route.from.name} → ${route.to.name}`,
        cacheKey
      })

      // Throttle to avoid rate limits (wait 2s between routes)
      await new Promise(resolve => setTimeout(resolve, 2000))
    } catch (error) {
      errorCount++
      logger.error('Failed to warm cache for route', {
        route: `${route.from.name} → ${route.to.name}`,
        error
      })
    }
  }

  const elapsedMs = Date.now() - startTime
  logger.info('Cache warming complete', {
    successCount,
    errorCount,
    totalRoutes: POPULAR_ROUTES.length,
    elapsedMs,
    elapsedMin: Math.round(elapsedMs / 60000)
  })
}

/**
 * Schedule cache warming for off-peak hours
 * Call this during server startup
 * 
 * ⚠️ COST WARNING: Disabled by default to save money
 * Cache warming = 10 routes × 365 days = 3,650 AI calls/year = $110/year wasted
 * Better alternative: Use seed-poi-database.mjs once = $0.06 total
 * 
 * Only enable if you need dynamic cache warming for NEW routes
 */
export function scheduleCacheWarming() {
  // Check if cache warming is enabled (opt-in, not opt-out)
  const enabled = process.env.ENABLE_CACHE_WARMING === 'true'
  if (!enabled) {
    logger.info('Cache warming disabled (use seed-poi-database.mjs instead for cost savings)')
    return
  }

  logger.info('Scheduling cache warming for 3:00 AM daily')

  // Calculate time until next 3:00 AM
  const now = new Date()
  const next3am = new Date()
  next3am.setHours(3, 0, 0, 0)
  
  if (now.getHours() >= 3) {
    // If it's past 3am today, schedule for tomorrow
    next3am.setDate(next3am.getDate() + 1)
  }

  const msUntil3am = next3am.getTime() - now.getTime()

  logger.info('Next cache warming scheduled', {
    nextRun: next3am.toISOString(),
    msUntil3am,
    hoursUntil: Math.round(msUntil3am / 3600000)
  })

  // Schedule first run
  setTimeout(() => {
    warmCache().catch(error => {
      logger.error('Cache warming failed', { error })
    })

    // Schedule recurring runs every 24 hours
    setInterval(() => {
      warmCache().catch(error => {
        logger.error('Cache warming failed', { error })
      })
    }, 24 * 60 * 60 * 1000) // 24 hours
  }, msUntil3am)
}
